/**
 * Modelo del wallet de la Home: unifica DOS fuentes reales en una sola lista de
 * movimientos ordenada por fecha:
 *
 *   1. Consumos  → sesiones de voz (`GET /api/modules/english/sessions`), cada
 *      una es un débito con su costo real denormalizado (`totalCostNano`).
 *   2. Recargas / bonos / ajustes → ledger de créditos sin sesión asociada
 *      (`GET /api/me/credits/transactions`, filtrando `sessionId === null`).
 *   3. El repaso de correcciones → una línea por día con lo cobrado
 *      (`GET /api/me/credits/review-days`). No es una charla: sus cobros no
 *      tienen sesión, y sueltos (unos 25 por repaso) taparían el ledger
 *      (Emmanuel, 15/10: «debemos cobrarlo todo, y debería estar registrado»).
 *
 * Todo acá es función pura (sin fetch, sin React) para poder testearlo. El
 * desglose por sesión NO se calcula acá: la UI reusa `SessionCostSheet`
 * (`GET /api/sessions/:id/cost`, datos reales).
 *
 * Montos en nano-USD (1e8 = $1), siempre string BigInt-safe. El display al
 * usuario es en orbs (`formatOrbs` de `@orborix/shared-types`).
 */
import { formatOrbs } from '@orborix/shared-types';
import type { CreditsBalance } from '../lib/billingApi.js';
import type { SessionHistoryItem } from '../lib/sessionsApi.js';
import type { LedgerTxn } from './walletApi.js';

export type WalletTxnKind =
  | 'conv'
  | 'review'
  | 'scenario'
  | 'program'
  | 'unlock'
  | 'grant'
  | 'refund'
  | 'adjust';
export type WalletTxnDir = 'in' | 'out';

export interface WalletTxn {
  id: string;
  kind: WalletTxnKind;
  dir: WalletTxnDir;
  label: string;
  /** Subtítulo corto (duración · contexto, o método de recarga). */
  sub: string;
  /** ISO timestamp — para ordenar y agrupar por bucket. */
  createdAt: string;
  /** Monto ABSOLUTO en nano-USD (string BigInt-safe). El signo lo da `dir`. */
  amountNano: string;
  /**
   * Si el movimiento es una sesión, su id (para abrir el desglose real con
   * `SessionCostSheet`). `null` para recargas/bonos/ajustes.
   */
  sessionId: string | null;
  /** Sólo para movimientos sin sesión: método crudo + estado, para el detalle. */
  meta?: { method: string | null; status: string };
}

function sessionToTxn(s: SessionHistoryItem): WalletTxn {
  const isScenario = s.scenarioId != null;
  // Mismo criterio que Activity (`isProgramSession`): sesión del Programa
  // guiado = modo plan sin escenario. Tiene identidad visual propia (naranja).
  const isProgram = !isScenario && s.mode === 'plan';
  const parts: string[] = [];
  // Minutos de charla (tiempo activo), no wall-clock: el mismo número que la
  // fila de Activity y el debrief de esa sesión. Con `durationSeconds` una
  // charla dejada abierta se anunciaba como "127 min" al lado de su costo real.
  // El piso de 1 minuto es para no decir "0 min" en una charla corta; una
  // sesión sin un solo turno (abierta y cerrada sin decir nada, que el toggle
  // "Sesiones vacías" del historial muestra) no tiene minutos que anunciar.
  if (s.activeSeconds != null && s.activeSeconds > 0) {
    const mins = Math.max(1, Math.round(s.activeSeconds / 60));
    parts.push(`${mins} min`);
  }
  parts.push(isScenario ? 'escenario' : isProgram ? 'programa' : 'coach');
  return {
    id: `s_${s.sessionId}`,
    kind: isScenario ? 'scenario' : isProgram ? 'program' : 'conv',
    dir: 'out',
    label: s.title,
    sub: parts.join(' · '),
    createdAt: s.createdAt,
    amountNano: s.totalCostNano,
    sessionId: s.sessionId,
  };
}

/**
 * Sub legible del unlock desde el notes del backend
 * (`listen unlock {book}/{edition}/{keypoint}`):
 * "atomic-habits/long/02-systems-over-goals" → "atomic habits · cap. 2".
 */
function unlockSub(notes: string | null): string {
  const m = /^listen unlock ([^/]+)\/[^/]+\/(\d+)?/.exec(notes ?? '');
  if (!m?.[1]) return 'Audioteca';
  const book = m[1].replace(/-/g, ' ');
  return m[2] ? `${book} · cap. ${Number(m[2])}` : book;
}

/** Lo cobrado por el repaso de correcciones en un día local (`/credits/review-days`). */
export interface ReviewDay {
  day: string;
  spentNano: string;
  count: number;
  lastAt: string;
}

function ledgerToTxn(t: LedgerTxn): WalletTxn | null {
  // Sólo movimientos SIN sesión. Los débitos de sesión ya vienen por la lista
  // de sesiones (con su costo denormalizado).
  if (t.sessionId != null) return null;
  if (t.kind === 'debit') {
    // Único débito sin sesión visible al user: el unlock de un capítulo de
    // /listen (Fase 22). Otros débitos huérfanos (jobs background del span
    // sink) se omiten — son detalle interno, no gasto accionable.
    if (t.callKind !== 'listen_unlock') return null;
    const amount = BigInt(t.amountNano);
    return {
      id: `t_${t.id}`,
      kind: 'unlock',
      dir: 'out',
      label: 'Capítulo desbloqueado',
      sub: unlockSub(t.notes),
      createdAt: t.createdAt,
      amountNano: (amount < 0n ? -amount : amount).toString(),
      sessionId: null,
      meta: { method: null, status: 'Completado' },
    };
  }

  const amount = BigInt(t.amountNano);
  const dir: WalletTxnDir = amount >= 0n ? 'in' : 'out';
  const absNano = (amount < 0n ? -amount : amount).toString();
  const label =
    t.kind === 'refund' ? 'Reembolso' : t.kind === 'adjust' ? 'Ajuste de saldo' : 'Crédito agregado';
  const fallbackSub = dir === 'in' ? 'Recarga de saldo' : 'Ajuste de saldo';

  return {
    id: `t_${t.id}`,
    kind: t.kind === 'refund' ? 'refund' : t.kind === 'adjust' ? 'adjust' : 'grant',
    dir,
    label,
    sub: t.notes ?? fallbackSub,
    createdAt: t.createdAt,
    amountNano: absNano,
    sessionId: null,
    meta: { method: t.notes, status: 'Completado' },
  };
}

/**
 * Mezcla sesiones (consumos) + ledger (recargas/bonos/ajustes) en una sola
 * lista de movimientos, ordenada por fecha descendente (más reciente primero).
 */
export function buildWalletTxns(
  sessions: readonly SessionHistoryItem[],
  ledger: readonly LedgerTxn[],
  reviewDays: readonly ReviewDay[] = [],
): WalletTxn[] {
  const out: WalletTxn[] = [];
  for (const s of sessions) out.push(sessionToTxn(s));
  for (const d of reviewDays) {
    out.push({
      id: `r_${d.day}`,
      kind: 'review',
      dir: 'out',
      label: 'Repaso de correcciones',
      sub: 'Voz del coach, preguntas y escucha',
      createdAt: d.lastAt,
      amountNano: d.spentNano,
      sessionId: null,
      meta: { method: null, status: 'Completado' },
    });
  }
  for (const t of ledger) {
    const mapped = ledgerToTxn(t);
    if (mapped) out.push(mapped);
  }
  out.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return out;
}

// ─── Agrupación por bucket temporal ──────────────────────────────────────────

export type Bucket = 'Hoy' | 'Ayer' | 'Esta semana' | 'Más antiguas';
export const BUCKET_ORDER: readonly Bucket[] = ['Hoy', 'Ayer', 'Esta semana', 'Más antiguas'];

const DAY_MS = 86_400_000;

function startOfDayMs(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function bucketOf(iso: string, now: Date): Bucket {
  const today0 = startOfDayMs(now);
  const d0 = startOfDayMs(new Date(iso));
  if (d0 === today0) return 'Hoy';
  if (d0 === today0 - DAY_MS) return 'Ayer';
  if (d0 > today0 - 7 * DAY_MS) return 'Esta semana';
  return 'Más antiguas';
}

export interface WalletGroup {
  bucket: Bucket;
  items: WalletTxn[];
}

/** Agrupa los movimientos por bucket, en el orden canónico, omitiendo vacíos. */
export function groupByBucket(txns: readonly WalletTxn[], now: Date): WalletGroup[] {
  return BUCKET_ORDER.map((bucket) => ({
    bucket,
    items: txns.filter((t) => bucketOf(t.createdAt, now) === bucket),
  })).filter((g) => g.items.length > 0);
}

const DOW = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MON = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Etiqueta de fecha de una fila: "Hoy · 09:14", "Ayer · 20:02", "lun · 18:30", "12 may". */
export function txnDateLabel(iso: string, now: Date): string {
  const d = new Date(iso);
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const bucket = bucketOf(iso, now);
  if (bucket === 'Hoy') return `Hoy · ${time}`;
  if (bucket === 'Ayer') return `Ayer · ${time}`;
  if (bucket === 'Esta semana') return `${DOW[d.getDay()]} · ${time}`;
  return `${d.getDate()} ${MON[d.getMonth()]}`;
}

/**
 * Saldo en orbs listo para mostrar en el sheet / historial (entero agrupado,
 * ej. "4.990"). Misma fuente que la pill del header (`formatOrbs(balanceNano)`).
 * El ledger sigue en nano-USD; orbs es sólo la denominación visible.
 */
export function balanceOrbsDisplay(balance: CreditsBalance | null): string {
  if (!balance) return formatOrbs(0n);
  return formatOrbs(balance.balanceNano);
}
